import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { TangramIcon } from "@/components/TangramIcon";
import { Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    if (pwd !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success(data.message);
      setDone(true);
      setTimeout(() => nav("/login"), 1800);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo actualizar la contraseña");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F1EA] px-6">
      <div className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-sm p-8 space-y-6">
        <div className="flex items-center gap-2.5 text-[#0A2540]">
          <TangramIcon size={22} outline stroke="#0A2540" />
          <span className="font-display text-lg font-extrabold tracking-tight">TANGRAM</span>
        </div>

        {!token ? (
          <div className="text-sm text-[#5C6A7D]" data-testid="reset-no-token">
            Enlace inválido o incompleto. Solicita uno nuevo desde <Link to="/login" className="text-[#0052CC] font-semibold">inicio de sesión</Link>.
          </div>
        ) : done ? (
          <div className="text-center space-y-3" data-testid="reset-done">
            <CheckCircle2 size={40} className="mx-auto text-[#10B981]" />
            <p className="text-sm text-[#0A2540] font-semibold">Contraseña actualizada.</p>
            <p className="text-xs text-[#5C6A7D]">Redirigiéndote al inicio de sesión…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Recuperación</div>
              <h2 className="font-display text-2xl font-extrabold tracking-tighter text-[#0A2540] mt-1">Nueva contraseña</h2>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Nueva contraseña</Label>
              <div className="relative">
                <Input type={showPwd ? "text" : "password"} value={pwd} onChange={(e) => setPwd(e.target.value)} className="rounded-sm h-11 pr-11" data-testid="reset-password-input" placeholder="••••••••" />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute inset-y-0 right-2 flex items-center justify-center w-8 text-[#5C6A7D] hover:text-[#0A2540] transition-colors"
                  data-testid="reset-password-toggle"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Confirmar contraseña</Label>
              <div className="relative">
                <Input type={showConfirm ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-sm h-11 pr-11" data-testid="reset-confirm-input" placeholder="••••••••" />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute inset-y-0 right-2 flex items-center justify-center w-8 text-[#5C6A7D] hover:text-[#0A2540] transition-colors"
                  data-testid="reset-confirm-toggle"
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-11" data-testid="reset-submit-button">
              {busy ? <Loader2 className="animate-spin" size={16} /> : "Actualizar contraseña"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
